'use client';

import React, { useState, useEffect, Fragment } from 'react';
import Map from './Map';
import MapMarker from './MapMarker';
import SavingsModal from './SavingsModal/SavingsModal';
import CrossmodModal from './CrossmodModal/CrossmodModal';
import { analytics } from '@/utils/analytics';

interface SavingsType {
  ebuilt: {
    cost: number;
  };
  traditional: {
    cost: number;
  };
  annual: {
    savings: number;
  };
}

interface Location {
  officeMap: {
    lat: number;
    lon: number;
  };
  officeName: string;
  officeCityState: string;
  officeButtons?: {
    primary?: {
      label?: string;
      url?: string;
      isRelative?: boolean;
      isOpenNewWindow?: boolean;
    };
  };
  officeDetail?: {
    utilities: {
      electric: SavingsType;
      gas: SavingsType;
      downloadUrl: string;
    };
  };
}

interface Props {
  locations: Location[];
  mapStyle: string;
}

const MapContainer = ({ locations, mapStyle }: Props) => {
  const [selectedLoc, setSelectedLoc] = useState<Location>();
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  const [mapZoomLevel, setMapZoomLevel] = useState(3);
  const [resizeTimeout] = useState(false);
  const [mapCenter, setMapCenter] = useState({
    lat: 35.8,
    lng: -98.38,
  });

  const checkUtilProps = (utility) => {
    return utility?.annual?.savings !== null &&
      utility?.ebuilt?.cost !== null &&
      utility?.traditional?.cost !== null
      ? utility
      : null;
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const winWidth =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      setIsMobile(winWidth < 1024);
      setMapZoomLevel(winWidth < 1024 ? 3 : 4.25);
    }

    const handleWindowResize = (e) => {
      if (resizeTimeout !== undefined && typeof resizeTimeout !== 'boolean') {
        clearTimeout(resizeTimeout);
      }

      const setResizeTimeout = window.setTimeout(
        () => setIsMobile(e.target.outerWidth < 1024),
        500
      );
      return setResizeTimeout;
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const resetMap = () => {
    setShowModal(false);
    setMapZoomLevel(isMobile ? 3 : 4.25);
    setMapCenter({
      lat: 35.8,
      lng: -98.38,
    });
  };

  return (
    <div className="smDesktop:relative">
      <div className="mt-[25px] h-[275px] w-full border border-solid border-grey-7 md:h-[300px] smDesktop:h-[500px]">
        <Map mapZoomLevel={mapZoomLevel} mapCenter={mapCenter}>
          {locations &&
            locations.map((loc, key) => {
              return (
                <MapMarker
                  key={`marker-${key}`}
                  location={{
                    latitude: loc.officeMap.lat,
                    longitude: loc.officeMap.lon,
                  }}
                  onClick={() => {
                    setSelectedLoc(loc);
                    setShowModal(true);
                    setMapZoomLevel(8);
                    setMapCenter({
                      lat: loc.officeMap.lat,
                      lng: isMobile
                        ? loc.officeMap.lon
                        : loc.officeMap.lon - 0.75,
                    });
                    analytics?.track('Map Location Clicked', {
                      location: loc.officeName,
                      mapSource:
                        mapStyle === 'ebuilt-savings'
                          ? 'ebuilt-savings'
                          : 'crossmod',
                    });
                  }}
                />
              );
            })}
        </Map>
      </div>
      {showModal && (
        <Fragment>
          {mapStyle === 'ebuilt-savings' ? (
            <SavingsModal
              closeSavingsModal={resetMap}
              cityState={selectedLoc?.officeName}
              downloadUrl={selectedLoc?.officeDetail?.utilities?.downloadUrl}
              cityElectric={checkUtilProps(
                selectedLoc?.officeDetail?.utilities?.electric
              )}
              cityGas={checkUtilProps(
                selectedLoc?.officeDetail?.utilities?.gas
              )}
            />
          ) : (
            selectedLoc !== undefined && (
              <CrossmodModal location={selectedLoc} closeModal={resetMap} />
            )
          )}
        </Fragment>
      )}
    </div>
  );
};

export default React.memo(MapContainer);
